import React, { useCallback, useEffect, useRef, useState } from 'react';
import TableList from '../../../../core/table/TableList';
import TableTop from '../../../../core/table/TableTop';
import TableHead from '../../../../core/table/TableHead';
import TableBody from '../../../../core/table/TableBody';
import TablePaging from '../../../../core/table/TablePaging';
import { useMonitorDefaultsTableInit } from './useMonitorDefaultsTableInit';
import { useMonitorDefaultsTableState } from './useMonitorDefaultsTableState';
import { useMonitorDefaultsData } from './useMonitorDefaultsData';
import TableSkeleton from '../../../../core/components/TableSkeleton';
import { useMountingSkeleton } from '../../../../core/hooks/useMountingSkeleton';
import MonitorDefaultsEditFlyOut from '../components/MonitorDefaultsEditFlyOut';
import { MonitorDefaultItem } from '../../data/datamonitorRepo';

type MonitorDefaultsTableProps = {
    canEdit: boolean;
};

const MonitorDefaultsTable = ({ canEdit }: MonitorDefaultsTableProps): JSX.Element => {
    const mounting = useMountingSkeleton();
    const { init, loadDefaults } = useMonitorDefaultsTableInit();
    const { waiting, cols, sections, sort, filters, fltStats, paging, setSort, setFilter, setPaging } =
        useMonitorDefaultsTableState();
    const { items } = useMonitorDefaultsData();

    const [editItem, setEditItem] = useState<MonitorDefaultItem | null>(null);
    const tableRef = useRef<HTMLTableElement>(null);

    useEffect(() => {
        if (!init) loadDefaults();
    }, [init, loadDefaults]);

    const handleRowClick = useCallback(
        (row: MonitorDefaultItem): void => {
            if (!canEdit) return;
            setEditItem(row);
        },
        [canEdit],
    );

    const handleClose = useCallback((): void => setEditItem(null), []);

    const handleSaved = useCallback((): void => {
        setEditItem(null);
        loadDefaults();
    }, [loadDefaults]);

    if (mounting || init || waiting) {
        return (
            <TableSkeleton
                columns={6}
                rows={10}
                sections={2}
            />
        );
    }

    return (
        <div className="monitor-defaults-table-wrapper">
            <TableList>
                <TableTop
                    id="monitorDefaultsTable"
                    fltStats={fltStats}
                />
                <table ref={tableRef}>
                    <TableHead
                        cols={cols}
                        sections={sections}
                        sort={sort}
                        filters={filters}
                        setFilter={setFilter}
                        setSort={setSort}
                    />
                    <TableBody
                        cols={cols}
                        rows={items}
                        onClick={canEdit ? handleRowClick : undefined}
                    />
                </table>
                <TablePaging
                    paging={paging}
                    setPaging={setPaging}
                    fltStats={fltStats}
                />
            </TableList>
            <MonitorDefaultsEditFlyOut
                item={editItem}
                onClose={handleClose}
                onSaved={handleSaved}
                ignoreClickRef={tableRef as React.RefObject<HTMLElement>}
            />
        </div>
    );
};

export default MonitorDefaultsTable;
